import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CoreService } from '@app/core/services/core.service';
import {
  Questionnaire,
  QuestionnaireTemplate,
} from '@app/core/models/questionnaire.model';
import { QuestionnaireTypeEnum } from '@app/core/enums/questionnaire.enums';
import { Question } from '@app/core/models/questionnaire.model';
import { EditingSection } from '../../questions/style-tab/section-editors/editing-section';
import type { PreviewContext } from '../../questions/style-tab/preview/preview.types';
import { WelcomeScreenPreviewComponent } from '../../questions/style-tab/preview/welcome-screen-preview/welcome-screen-preview';
import { HeaderSectionPreviewComponent } from '../../questions/style-tab/preview/header-section-preview/header-section-preview';
import { PageSectionPreviewComponent } from '../../questions/style-tab/preview/page-section-preview/page-section-preview';
import { BodyContentPreviewComponent } from '../../questions/style-tab/preview/body-content-preview/body-content-preview';
import { FooterSectionPreviewComponent } from '../../questions/style-tab/preview/footer-section-preview/footer-section-preview';
import { ExitScreenPreviewComponent } from '../../questions/style-tab/preview/exit-screen-preview/exit-screen-preview';

type PreviewScreen = 'welcome' | 'pageLayout' | 'exit';

@Component({
  selector: 'app-mobile',
  standalone: true,
  imports: [
    CommonModule,
    WelcomeScreenPreviewComponent,
    HeaderSectionPreviewComponent,
    PageSectionPreviewComponent,
    BodyContentPreviewComponent,
    FooterSectionPreviewComponent,
    ExitScreenPreviewComponent,
  ],
  templateUrl: './mobile.html',
  styleUrl: './mobile.scss',
})
export class Mobile {
  @Output() addQuestionClick = new EventEmitter<void>();
  @ViewChild('bodySectionRef', { read: ElementRef }) bodySectionRef?: ElementRef<HTMLElement>;

  @Input() section: 'THEME' | 'QUESTION' = 'QUESTION';
  questionnaire: Questionnaire | null = null;
  previewTemplate: QuestionnaireTemplate | null = null;
  previewScreen: PreviewScreen = 'welcome';
  currentPage = 0;
  /** Mobile view always uses 'mobile' for preview components */
  readonly viewMode: 'web' | 'mobile' = 'mobile';
  EditingSection = EditingSection;

  constructor(private coreService: CoreService) {
    this.coreService.questionnaireDeatils$.subscribe((q) => {
      if (q) {
        this.questionnaire = q;
        this.buildPreviewTemplate(q);
        this.resetToStart();
      }
    });
  }

  scrollPreviewToSelectedSection(): void {
    if (!this.bodySectionRef) return;
    this.bodySectionRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  }
  private buildPreviewTemplate(q: Questionnaire): void {
    const hf = q.headerFooterSetup;
    const headerOn =
      (hf as { enableHeader?: boolean })?.enableHeader === true ||
      hf?.enableHeader === true;
    const footerOn =
      (hf as { enableFooter?: boolean })?.enableFooter === true ||
      hf?.enableFooter === true;

    const ws = q.welcomeScreenTemplate;
    const welcomeOn =
      (ws as { enableWelcomeScreen?: boolean })?.enableWelcomeScreen !== false;

    const es = q.exitScreenTemplate;
    const exitOn =
      (es as { enableExitScreen?: boolean })?.enableExitScreen !== false;

    this.previewTemplate = {
      templateId: q.brandingTemplateId || '',
      templateName: q.brandingTemplateName || 'Preview',
      ...(ws && welcomeOn && {
        welcomeScreenTemplate: JSON.parse(JSON.stringify(ws)),
      }),
      ...(es && exitOn && {
        exitScreenTemplate: JSON.parse(JSON.stringify(es)),
      }),
      mainContentTemplate: JSON.parse(
        JSON.stringify(q.mainContentTemplate || {})
      ),
      ...(q.pageTemplate && {
        pageTemplate: JSON.parse(JSON.stringify(q.pageTemplate)),
      }),
      ...(hf && (headerOn || footerOn) && {
        headerFooterSetup: JSON.parse(JSON.stringify(hf)),
      }),
    } as QuestionnaireTemplate;
  }

  getPreviewContext(): PreviewContext {
    const editingSection =
      this.previewScreen === 'welcome'
        ? EditingSection.WELCOME_SCREEN
        : this.previewScreen === 'exit'
          ? EditingSection.EXIT_SCREEN
          : EditingSection.BODY_CONTENT;
    return {
      questionnaire: this.questionnaire,
      editingTemplate: null,
      editingTemplateId: null,
      editingSection,
      selectedTemplateId: this.questionnaire?.brandingTemplateId ?? null,
      previewTemplate: this.previewTemplate,
      currentTemplate: this.previewTemplate,
      currentPage: this.currentPage,
      getCurrentPageQuestions: () => this.getCurrentPageQuestions(),
      getDisplayPageNumber: () => this.getDisplayPageNumber(),
      getDisplayTotalPages: () => this.getDisplayTotalPages(),
      canGoToNextPage: () => this.canGoToNextPage(),
      canGoToPreviousPage: () => this.canGoToPreviousPage(),
      shouldShowUserInfo: () => this.shouldShowUserInfo(),
    };
  }

  startQuestionnairePreview(): void {
    this.previewScreen = 'pageLayout';
    this.initializeCurrentPage();
    // setTimeout(() => {
      this.scrollPreviewToSelectedSection();
    // }, 150);
  }

  /** True when the mobile device preview should show the welcome step (template exists and not explicitly disabled). */
  private welcomePreviewShouldShow(): boolean {
    const ws = this.questionnaire?.welcomeScreenTemplate;
    if (!ws) return false;
    return (
      (ws as { enableWelcomeScreen?: boolean }).enableWelcomeScreen !== false
    );
  }

  resetToStart(): void {
    if (this.welcomePreviewShouldShow()) {
      this.previewScreen = 'welcome';
    } else {
      this.previewScreen = 'pageLayout';
      this.initializeCurrentPage();
    }
  }

  getTotalPages(): number {
    if (!this.questionnaire?.questions?.length) return 0;
    return Math.max(
      ...this.questionnaire.questions.map((q) => q.pageNumber || 1),
      0
    );
  }

  getQuestionsByPage(pageNumber: number): Question[] {
    if (!this.questionnaire?.questions || pageNumber === 0) return [];
    return this.questionnaire.questions.filter(
      (q) => (q.pageNumber || 1) === pageNumber
    );
  }

  getCurrentPageQuestions(): Question[] {
    if (this.currentPage === 0) return [];
    return this.getQuestionsByPage(this.currentPage);
  }

  getDisplayPageNumber(): number {
    return this.currentPage;
  }

  getDisplayTotalPages(): number {
    return this.getTotalPages();
  }

  canGoToNextPage(): boolean {
    const maxQuestionPage = this.getTotalPages();
    if (this.currentPage === 0) return maxQuestionPage > 0;
    return this.currentPage <= maxQuestionPage;
  }

  canGoToPreviousPage(): boolean {
    return this.currentPage > 1;
  }

  shouldShowUserInfo(): boolean {
    if (this.questionnaire?.questionnaireType !== QuestionnaireTypeEnum.SURVEY)
      return false;
    const status =
      this.questionnaire?.userInfoCaptureSettings?.statusCondition || 'ANONYMOUS';
    return status === 'OPTIONAL' || status === 'MANDATORY';
  }

  initializeCurrentPage(): void {
    if (this.shouldShowUserInfo()) {
      this.currentPage = 0;
    } else {
      this.currentPage = this.getTotalPages() > 0 ? 1 : 0;
    }
  }

  goToNextPage(): void {
    const maxQuestionPage = this.getTotalPages();
    if (this.currentPage === 0) {
      this.currentPage = 1;
    } else if (this.currentPage < maxQuestionPage) {
      this.currentPage++;
    } else if (this.currentPage === maxQuestionPage && maxQuestionPage > 0) {
      this.previewScreen = 'exit';
    }
    // setTimeout(() => {
      this.scrollPreviewToSelectedSection();
    // }, 150);
  }

  goToPreviousPage(): void {
    if (this.currentPage === 1 && this.shouldShowUserInfo()) {
      this.currentPage = 0;
    } else if (this.currentPage > 1) {
      this.currentPage--;
    }
    // setTimeout(() => {
      this.scrollPreviewToSelectedSection();
    // }, 150);
  }

  goToPage(page: number): void {
    const maxQuestionPage = this.getTotalPages();
    if (page === 0 && this.shouldShowUserInfo()) {
      this.currentPage = 0;
    } else if (page >= 1 && page <= maxQuestionPage) {
      this.currentPage = page;
    }
    // setTimeout(() => {
      this.scrollPreviewToSelectedSection();
    // }, 150);
  }

  onAddQuestion(): void {
    this.addQuestionClick.emit();
  }
}
